/** @jsx jsx */
import { jsx, Container, Link, Flex, Box, Image } from "theme-ui";
import { Themed } from "@theme-ui/mdx";
import { Link as GatsbyLink } from "gatsby";

import PageSection from "./PageSection";
import Newsletter from "./NewsletterOctopus-official";
import Nav from "./Nav";
import EmailLogo from "../../content/assets/icons/envelope-solid.inline.svg";
import BlueSkyLogo from "../../content/assets/icons/bluesky.inline.svg";
import filesideLogo from "../../content/assets/fileside-logo-24.png";
import GridContainer from "./GridContainer";

const SmallLink = ({children, href, ...rest}) => (
  <Link 
    href={href}
    sx={{ 
      color: "inherit", 
      textDecoration: "underline" 
    }}
    {...rest}
  >{children}</Link>
);

const Footer = () => {
  return (
    <Box as="footer" sx={{
      background: (theme) => `linear-gradient(
        to top,
        ${theme.colors.primaryDark},
        ${theme.colors.secondaryDark} 50%
      )`,
    }}>
      {/* Main footer with links and subscribe */}
      <PageSection 
        sx={{
          pt: [4, 5],
          pb: [7, 8]
        }}
      >
        <GridContainer columns={[1, 1, 3]} gap={[0, 0, 7]}>
          <Box>
            <Themed.h3 sx={{color: "textLight.2"}}>Navigate</Themed.h3>
            <Nav sx={{
              flexDirection: ["row", "row", "column"],
              gap: [2, 4, 1]
            }} />
          </Box>

          <Flex sx={{ flexDirection: "column" }}>
            <Themed.h3 sx={{color: "textLight.2"}}>Connect</Themed.h3>
            <Flex sx={{flexDirection: ["row", "row", "column"]}}>
              <div sx={{ mr: [6, 6, 0]}}>
                <div 
                  sx={{color: "textLight.4", fontSize: "control", mb: "1px"}}
                >
                  Send email to
                </div>
                <Link 
                  variant="nav" 
                  href="mailto:erik@fileside.app"
                  target="_blank"
                  sx={{
                    fontSize: "body",
                    mb: 4,
                    pl: "1px",
                    whiteSpace: "pre",
                  }}
                >
                  <EmailLogo sx={{
                    position: "relative",
                    top: "4px",
                    height: "18px", 
                    mr: 2
                  }} /> 
                  erik@fileside.app
                </Link>
              </div>
              <div>
                <div 
                  sx={{color: "textLight.4", fontSize: "control", mb: "1px"}}
                >
                  Follow development at
                </div>
                <Link 
                  variant="nav" 
                  href="https://bsky.app/profile/erkjlvk.bsky.social"
                  target="_blank"
                  sx={{
                    fontSize: "body",
                    pl: "1px",
                    whiteSpace: "pre",
                  }}
                >
                  <BlueSkyLogo sx={{
                    position: "relative",
                    top: "4px",
                    height: "18px", 
                    mr: 2
                  }} /> 
                erkjlvk.bsky.social
                </Link>
              </div>
            </Flex>
          </Flex>

          <Box>
            <Themed.h3 sx={{color: "textLight.2"}}>Subscribe</Themed.h3>
            <Newsletter orientation={["column", "row", "column"]} />
          </Box>
        </GridContainer>
      </PageSection>

      {/* Fineprint line */}
      <PageSection 
        sx={{
          pt: [2, 3],
          pb: [3, 5]
        }}
      >
        <Container sx={{
          display: "flex", 
          flexDirection: ["column", "column", "row"], 
          alignItems: "center",
          gap: 2,
          justifyContent: "space-between"
        }}>
          <Flex sx={{ flexDirection: "row", alignItems: "center" }}>
            <GatsbyLink to="/" sx={{
              textDecoration: "none", 
              lineHeight: 1,
              mr: 2,
              "&:hover": { filter: "brightness(110%)" },
              "&:focus": { outline: 0 }
            }}>
              <Image src={filesideLogo} sx={{width: 24}} />
            </GatsbyLink>

            <span sx={{fontSize: "tiny", color: "textLight.3"}}>
              Built <SmallLink href="https://built.organic/" target="_blank">organically</SmallLink> by <SmallLink href="/about">Erik Jälevik</SmallLink> in Berlin.
            </span>
          </Flex>

          <Flex sx={{ flexDirection: "row", alignItems: "center"}}>
            <GatsbyLink to="/legal/" sx={{variant: "links.nav", textAlign: "center", fontSize: "tiny", mb: [1, 0]}}>
              Privacy, Legal &amp; Impressum&nbsp;
            </GatsbyLink>
            <span sx={{
              fontSize: "tiny", 
              textAlign: "center", 
              color: "textLight.3",
              mx: 2
            }}>
              |
            </span>
            <span sx={{fontSize: "tiny", textAlign: "center", color: "textLight.3"}}>
              © {new Date().getFullYear()} All rights reserved
            </span>
          </Flex>
        </Container>
      </PageSection>  
    </Box>
  );
};

export default Footer;